import React, { useEffect, useRef, useState } from 'react';
import { fmtQty, psApi } from './psApi';
import { PsBtnIcon } from './psIcons';

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export function Modal({ title, wide, onClose, children, footer }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="ps-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'ps-modal' + (wide ? ' ps-modal--wide' : '')} role="dialog" aria-modal="true" aria-label={title}>
        <div className="ps-mhead">
          <h2>{title}</h2>
          <button type="button" className="ps-x" aria-label="Close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="ps-mbody">{children}</div>
        {footer && <div className="ps-mfoot">{footer}</div>}
      </div>
    </div>
  );
}

/** "Are you sure?" for every delete. `typeWord` (e.g. 'DELETE') must be typed before the button enables. */
function ConfirmModal({ title, message, confirmLabel = 'Delete', typeWord, onAnswer }) {
  const [word, setWord] = useState('');
  const ok = !typeWord || word === typeWord;
  return (
    <Modal title={title || 'Are you sure?'} onClose={() => onAnswer(false)} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={() => onAnswer(false)}>Cancel</button>
      <button type="button" className="ps-btn ps-btn--danger" disabled={!ok} autoFocus={!typeWord}
        onClick={() => onAnswer(true)}>{confirmLabel}</button>
    </>}>
      <p>{message}</p>
      {typeWord && (
        <label className="ps-field">Type <b>{typeWord}</b> to confirm
          <input value={word} onChange={(e) => setWord(e.target.value)} autoFocus aria-label={`Type ${typeWord} to confirm`}
            onKeyDown={(e) => { if (e.key === 'Enter' && ok) onAnswer(true); }} />
        </label>
      )}
    </Modal>
  );
}

/** `const [confirmEl, ask] = useConfirm();` — render `confirmEl`, then `if (!(await ask({ message }))) return;`. */
export function useConfirm() {
  const [req, setReq] = useState(null);
  const ask = (opts) => new Promise((resolve) => setReq({ ...opts, resolve }));
  const el = req && (
    <ConfirmModal {...req} onAnswer={(yes) => { req.resolve(yes); setReq(null); }} />
  );
  return [el, ask];
}

/** Search parts, the tenant's own assembly type (when set) and DMS items — `GET /structures/search`. */
export function ItemPicker({ picked, onPick, allowDocs = true, note }) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    const my = ++seq.current;
    const t = setTimeout(async () => {
      setBusy(true);
      setError('');
      try {
        const res = await psApi.search(query, allowDocs);
        if (my !== seq.current) return;
        setRows(res.rows.map((r) => ({ node: r.node, type: r.type, key: r.key, name: r.name,
          label: r.type !== 'PRT' && r.type !== 'DMS' ? r.label : '' })));
        if (res.errors.length) setError(res.errors.join(' '));
      } catch (e) {
        if (my === seq.current) setError(e.message);
      } finally {
        if (my === seq.current) setBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, allowDocs]);

  return (
    <>
      <input className="ps-search" type="search" placeholder="Search parts and assemblies by key or name" aria-label="Search parts and assemblies"
        value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      {error && <div className="ps-error">{error}</div>}
      <div className="ps-candlist">
        {busy && !rows.length && <div className="ps-muted ps-small">Searching…</div>}
        {!busy && !rows.length && !error && <div className="ps-muted ps-small">Nothing found.</div>}
        {rows.map((r) => {
          const n = note ? note(r) : null;
          return (
            <button key={r.node} type="button" className={'ps-cand' + (picked?.node === r.node ? ' ps-cand--on' : '')}
              disabled={!!n?.disabled} onClick={() => onPick(r)}>
              <span className="ps-ikey">{r.key}</span><span>{r.name}</span>
              {(n?.text || r.type === 'DMS' || r.label) && <span className="ps-cand-note">{n?.text || (r.type === 'DMS' ? 'Document · leaf' : r.label)}</span>}
            </button>
          );
        })}
      </div>
    </>
  );
}

export function AddChildModal({ parent, shared, inBranch, onClose, onAdd }) {
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState('1');
  const [findNo, setFindNo] = useState('');
  const [refDes, setRefDes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const q = parseFloat(qty);
  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await onAdd({ child: picked.node, qty: q, find_no: findNo, ref_des: refDes, label: picked.key });
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <Modal title={`Add under ${parent.key}`} onClose={onClose} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="button" className="ps-btn" disabled={!picked || !(q > 0) || busy} onClick={submit}>{busy ? 'Adding…' : 'Add'}</button>
    </>}>
      {inBranch && <div className="ps-banner ps-banner--br">This goes into the branch only, until it is merged.</div>}
      {!inBranch && shared > 1 && (
        <div className="ps-banner ps-banner--warn">{parent.key} is shared by {shared} products. What you add here appears in all of them.</div>
      )}
      <ItemPicker picked={picked} onPick={setPicked}
        note={(r) => (r.node === parent.node ? { disabled: true, text: 'Would create a loop' } : null)} />
      <div className="ps-row">
        <label className="ps-field">Quantity<input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} /></label>
        <label className="ps-field">Find no.<input type="text" value={findNo} onChange={(e) => setFindNo(e.target.value)} /></label>
        <label className="ps-field">Ref. des.<input type="text" value={refDes} onChange={(e) => setRefDes(e.target.value)} /></label>
      </div>
      {error && <div className="ps-error" role="alert">{error}</div>}
    </Modal>
  );
}

export function NewProductModal({ existing = [], onClose, onAdd }) {
  const have = new Set(existing);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <Modal title="New product structure" onClose={onClose} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="button" className="ps-btn" disabled={!picked || busy}
        onClick={async () => { setBusy(true); try { await onAdd(picked); } catch (e) { setError(e.message); setBusy(false); } }}>
        {busy ? 'Adding…' : 'Add product'}
      </button>
    </>}>
      <p className="ps-muted ps-small">Pick the Orcanos part that is the product (the top of the structure). Its parts are added from the tree.</p>
      <ItemPicker picked={picked} onPick={setPicked} allowDocs={false}
        note={(r) => (have.has(r.node) ? { disabled: true, text: 'Already a product' } : null)} />
      {error && <div className="ps-error" role="alert">{error}</div>}
    </Modal>
  );
}

export function MoveModal({ row, candidates, onClose, onMove }) {
  const [target, setTarget] = useState('');
  const [error, setError] = useState('');
  return (
    <Modal title={`Move ${row.key}`} onClose={onClose} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="button" className="ps-btn" disabled={!target}
        onClick={async () => { try { await onMove(target); } catch (e) { setError(e.message); } }}>Move</button>
    </>}>
      <p className="ps-muted ps-small">Moves this line, with its quantity, under another assembly. The new parent's other products get it too if that parent is shared.</p>
      <div className="ps-candlist">
        {candidates.map((c) => (
          <button key={c.node} type="button" className={'ps-cand' + (target === c.node ? ' ps-cand--on' : '')}
            disabled={c.disabled} onClick={() => setTarget(c.node)}>
            <span className="ps-ikey">{c.key}</span><span>{c.name}</span>
            {c.note && <span className="ps-cand-note">{c.note}</span>}
          </button>
        ))}
      </div>
      {error && <div className="ps-error" role="alert">{error}</div>}
    </Modal>
  );
}

export function WhereUsedModal({ data, onClose, onLocate }) {
  return (
    <Modal title={`Where used — ${data.item.key}`} onClose={onClose}
      footer={<button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Close</button>}>
      <p className="ps-muted ps-small">{data.item.name} · every path from a product down to it, with the quantity in one product.</p>
      {data.paths.length === 0 && <p className="ps-muted">Not used in any product structure.</p>}
      {data.paths.map((p) => (
        <div key={p.root + p.rk} className="ps-wurow">
          <span>{p.keys.join(' › ')}</span>
          <span><b>× {fmtQty(p.qty)}</b> <button type="button" className="ps-btn ps-btn--link" onClick={() => onLocate(p.root, data.item.node)}>Locate</button></span>
        </div>
      ))}
    </Modal>
  );
}

export function BaselineModal({ rootLabel, onClose, onSave }) {
  const [name, setName] = useState('');
  const [ver, setVer] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return (
    <Modal title={`Take baseline — ${rootLabel}`} onClose={onClose} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="button" className="ps-btn" disabled={!name.trim() || busy}
        onClick={async () => { setBusy(true); try { await onSave({ name, version_label: ver, note }); } catch (e) { setError(e.message); setBusy(false); } }}>
        <PsBtnIcon name="takeBaseline" />{busy ? 'Taking…' : 'Take baseline'}
      </button>
    </>}>
      <p className="ps-muted ps-small">Freezes the whole structure, with each part&rsquo;s current Orcanos revision and status, and the rolled-up cost.</p>
      <label className="ps-field">Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Design freeze" autoFocus /></label>
      <label className="ps-field">Version label<input value={ver} onChange={(e) => setVer(e.target.value)} placeholder="e.g. 1.2" /></label>
      <label className="ps-field">Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></label>
      {error && <div className="ps-error" role="alert">{error}</div>}
    </Modal>
  );
}

export function NewBranchModal({ rootLabel, from, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('proposal');
  const [error, setError] = useState('');
  return (
    <Modal title={`New branch of ${rootLabel}`} onClose={onClose} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="button" className="ps-btn" disabled={!name.trim()}
        onClick={async () => { try { await onCreate({ name, kind }); } catch (e) { setError(e.message); } }}>Create branch</button>
    </>}>
      {from && (
        <p className="ps-bp-from">Starts from branch <b>{from.name}</b> with its {from.changes.length} change{from.changes.length === 1 ? '' : 's'}.
          Like every branch it still sits on main, and merges to main.</p>
      )}
      <label className="ps-field">Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. US variant, Supplier change" autoFocus /></label>
      <div className="ps-field">Kind
        <div className="ps-seg">
          {[['proposal', 'Change proposal'], ['variant', 'Variant']].map(([k, l]) => (
            <button key={k} type="button" className={kind === k ? 'ps-seg--on' : ''} aria-pressed={kind === k} onClick={() => setKind(k)}>{l}</button>
          ))}
        </div>
      </div>
      <p className="ps-muted ps-small">{kind === 'variant'
        ? 'A variant lives on beside main (e.g. a country version). Main’s changes always show in it; merge single changes back when they apply to every version.'
        : 'A change proposal is a copy to try a change on, compare, then merge to main.'}</p>
      {error && <div className="ps-error" role="alert">{error}</div>}
    </Modal>
  );
}

export function MergeModal({ branch, rootLabel, onClose, onMerge }) {
  const [scopes, setScopes] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const shared = branch.changes.filter((c) => c.shared && !c.conflict);
  const undecided = shared.filter((c) => !scopes[c.index]).length;
  const conflicts = branch.changes.filter((c) => c.conflict).length;
  return (
    <Modal wide title={`Merge “${branch.name}” to main`} onClose={onClose} footer={<>
      <span className="ps-muted ps-small" style={{ marginRight: 'auto' }}>
        {undecided ? `${undecided} shared change${undecided > 1 ? 's' : ''} still need a choice` : 'Ready to merge'}
      </span>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="button" className="ps-btn" disabled={undecided > 0 || busy}
        onClick={async () => { setBusy(true); try { await onMerge(scopes); } catch (e) { setError(e.message); setBusy(false); } }}>
        {busy ? 'Merging…' : 'Merge'}
      </button>
    </>}>
      <p className="ps-muted ps-small">A change under a sub-assembly that other products use needs a choice: change it in every product, or give {rootLabel} its own version of that sub-assembly.</p>
      {conflicts > 0 && <div className="ps-banner ps-banner--warn">{conflicts} change{conflicts > 1 ? 's' : ''} can&rsquo;t be merged — the line is no longer in main. {conflicts > 1 ? 'They stay' : 'It stays'} in the branch.</div>}
      {branch.changes.map((c) => (
        <div key={c.index} className="ps-mchg">
          <div className="ps-row" style={{ justifyContent: 'space-between' }}>
            <b>{c.text}</b><span className="ps-ikey ps-small">under {c.under}</span>
          </div>
          {c.conflict && <span className="ps-st ps-st--bad">No longer in main — skipped</span>}
          {!c.conflict && c.shared && (
            <>
              <div className="ps-banner ps-banner--warn" style={{ margin: 0 }}>
                {c.under} is used in: {c.used_in.map((u) => u.name || u.key).join(' · ')}
              </div>
              <div className="ps-seg">
                <button type="button" className={scopes[c.index] === 'all' ? 'ps-seg--on' : ''} aria-pressed={scopes[c.index] === 'all'}
                  onClick={() => setScopes((s) => ({ ...s, [c.index]: 'all' }))}>Change everywhere</button>
                <button type="button" className={scopes[c.index] === 'only' ? 'ps-seg--on' : ''} aria-pressed={scopes[c.index] === 'only'}
                  onClick={() => setScopes((s) => ({ ...s, [c.index]: 'only' }))}>Only in {rootLabel}</button>
              </div>
            </>
          )}
          {!c.conflict && !c.shared && <span className="ps-muted ps-small">Not shared — applies to {rootLabel} only.</span>}
        </div>
      ))}
      {error && <div className="ps-error" role="alert">{error}</div>}
    </Modal>
  );
}

export function PullModal({ data, onClose, onAccept }) {
  return (
    <Modal title="Changes on main since this branch" onClose={onClose} footer={<>
      <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>Close</button>
      {data.main_changes.length > 0 && <button type="button" className="ps-btn" onClick={onAccept}>Mark as seen</button>}
    </>}>
      <p className="ps-muted ps-small">The branch always shows its changes on top of the current main, so these are already in it.</p>
      {data.main_changes.length === 0 && <p>Main has not changed since this branch last looked — nothing to pull.</p>}
      {data.main_changes.map((r) => (
        <div key={r.node} className="ps-wurow">
          <span><span className="ps-ikey">{r.key}</span> {r.name}</span>
          <span className={`ps-chg ps-chg--${r.kind}`}>
            {r.kind === 'added' ? `Added × ${fmtQty(r.to_qty)}` : r.kind === 'removed' ? 'Removed' : `Qty ${fmtQty(r.from_qty)} → ${fmtQty(r.to_qty)}`}
          </span>
        </div>
      ))}
      {data.conflicts.length > 0 && (
        <div className="ps-banner ps-banner--warn">{data.conflicts.length} of the branch&rsquo;s changes point at a line main removed. Drop {data.conflicts.length > 1 ? 'them' : 'it'} from the branch&rsquo;s change list.</div>
      )}
    </Modal>
  );
}
